/**
 * The first-launch environment report (epic PI-SPEC-SW-E279).
 *
 * SpecTree Toolbox runs the checks; SpecTree's `/toolbox/setup` page shows
 * them. The report crosses a version boundary (the app and the page release
 * separately), so the page parses it with {@link environmentReportSchema}.
 *
 * Shared checks apply to every toolset. Each toolset adds its own checks,
 * keyed by toolset id. A toolset is ready when the shared checks and that
 * toolset's required checks are `ok`; `info` checks never block.
 */
import { z } from "zod";
/** `ok` passed; `fail` blocks and has a fix; `info` is shown but never blocks. */
export const ENVIRONMENT_CHECK_STATUSES = ["ok", "fail", "info"];
/** One check's result. */
export const environmentCheckSchema = z.object({
    status: z.enum(ENVIRONMENT_CHECK_STATUSES),
    /** One plain sentence telling the person what to do. Set when `status` is not `ok`. */
    fix: z.string().max(500).optional(),
});
/** Checks every toolset needs. */
export const SHARED_ENVIRONMENT_CHECKS = [
    "claude-installed",
    "claude-signed-in",
    "spectree-account",
    "spectree-contract",
];
/** The grooming toolset's checks. `board-auditor` is informational. */
export const GROOMING_ENVIRONMENT_CHECKS = ["ado-pat", "board-auditor", "grooming-tools"];
/** The grooming checks that must be `ok` before grooming can run. */
export const GROOMING_REQUIRED_CHECKS = ["ado-pat", "grooming-tools"];
/**
 * The formulator toolset's checks (contract formulator 1). `formulator-tools`
 * means the plugin skill and the SpecTree MCP tools the interview uses are
 * available to Claude Code. No board-auditor check: anyone with an ADO PAT
 * may write an item.
 */
export const FORMULATOR_ENVIRONMENT_CHECKS = ["ado-pat", "formulator-tools"];
/** The formulator checks that must be `ok` before an interview can run. */
export const FORMULATOR_REQUIRED_CHECKS = ["ado-pat", "formulator-tools"];
/**
 * The help toolset's checks (contract help 1, epic PI-SPEC-SW-E302).
 * `help-tools` means the `toolbox-help` plugin skill and the bundled help
 * pack are available to Claude Code. No ADO PAT check: everyone signed in to
 * the Toolbox may open Help (Resolved Decision 4). The Help panel reads its
 * own readiness from `help.getStatus()`; this group only feeds Setup.
 */
export const HELP_ENVIRONMENT_CHECKS = ["help-tools"];
/** The help checks that must be `ok` before Help can answer. */
export const HELP_REQUIRED_CHECKS = ["help-tools"];
/**
 * The fogbugz toolset's checks (contract fogbugz 1, epic PI-SPEC-SW-E307,
 * ER-1663). `fogbugz-tools` means the bundled plugin with the FogBugz EVTX
 * summary skill is available to Claude Code. Claude Code found and signed in
 * on a subscription seat are the shared `claude-installed` and
 * `claude-signed-in` checks; a run that would bill an API key is refused by
 * the engine with `API_KEY_BILLING`. No repository-copy and no ADO PAT
 * checks: the summary is written from the digest alone, and FogBugz access
 * is the person's own key saved in SpecTree.
 */
export const FOGBUGZ_ENVIRONMENT_CHECKS = ["fogbugz-tools"];
/** The fogbugz checks that must be `ok` before an EVTX summary can run. */
export const FOGBUGZ_REQUIRED_CHECKS = ["fogbugz-tools"];
/**
 * The fogbugz-formulator toolset's checks (contract fogbugz-formulator 1,
 * epic PI-SPEC-SW-E309). `fogbugz-formulator-tools` means the bundled plugin
 * with the `toolbox-fogbugz-formulator` skill is available to Claude Code.
 * No ADO PAT check: FogBugz access is the person's own key saved in
 * SpecTree. The linked ADO project's repository copies are checked per
 * session by `fogbugzFormulator.start` (`REPO_NOT_SET_UP`), not here, because
 * they depend on the FogBugz project the person routes to.
 */
export const FOGBUGZ_FORMULATOR_ENVIRONMENT_CHECKS = ["fogbugz-formulator-tools"];
/** The fogbugz-formulator checks that must be `ok` before an interview can run. */
export const FOGBUGZ_FORMULATOR_REQUIRED_CHECKS = [
    "fogbugz-formulator-tools",
];
/**
 * The ask toolset's checks (contract ask 1, epic PI-SPEC-SW-E310, ER-1667
 * Package F). `ask-tools` means the bundled plugin with the Product Q&A skill
 * is available to Claude Code. The product's repository copies are checked
 * per product by `ask.checkProduct` (`REPO_NOT_COPIED`, ...), not here,
 * because they depend on the product the person picks.
 */
export const ASK_ENVIRONMENT_CHECKS = ["ask-tools"];
/** The ask checks that must be `ok` before Product Q&A can answer. */
export const ASK_REQUIRED_CHECKS = ["ask-tools"];
/**
 * The fogbugz-to-ado toolset's checks (contract fogbugz-to-ado 1, epic
 * PI-SPEC-SW-E311, ER-1666 Package E). `fogbugz-to-ado-tools` means the
 * bundled plugin with the fogbugz-to-ado skill is available to Claude Code.
 * Claude Code signed in on a subscription seat is the shared
 * `claude-installed` and `claude-signed-in` checks (a run that would bill an
 * API key is refused with `API_KEY_BILLING`). The linked ADO project's
 * repository copies are checked per case by `checkRepos`
 * (`REPOS_NOT_COPIED`), as the FogBugz Formulator does per session, because
 * they depend on the case's FogBugz project. No ADO PAT check here: SpecTree
 * refuses the case with `ADO_PAT_MISSING` before any Claude run.
 */
export const FOGBUGZ_TO_ADO_ENVIRONMENT_CHECKS = ["fogbugz-to-ado-tools"];
/** The fogbugz-to-ado checks that must be `ok` before a case can be read or drafted. */
export const FOGBUGZ_TO_ADO_REQUIRED_CHECKS = ["fogbugz-to-ado-tools"];
/** Required check ids per toolset. */
export const TOOLSET_REQUIRED_CHECKS = {
    grooming: GROOMING_REQUIRED_CHECKS,
    formulator: FORMULATOR_REQUIRED_CHECKS,
    help: HELP_REQUIRED_CHECKS,
    fogbugz: FOGBUGZ_REQUIRED_CHECKS,
    "fogbugz-formulator": FOGBUGZ_FORMULATOR_REQUIRED_CHECKS,
    ask: ASK_REQUIRED_CHECKS,
    "fogbugz-to-ado": FOGBUGZ_TO_ADO_REQUIRED_CHECKS,
};
const sharedChecksSchema = z.object({
    "claude-installed": environmentCheckSchema,
    "claude-signed-in": environmentCheckSchema,
    "spectree-account": environmentCheckSchema,
    "spectree-contract": environmentCheckSchema,
});
const groomingChecksSchema = z.object({
    "ado-pat": environmentCheckSchema,
    "board-auditor": environmentCheckSchema,
    "grooming-tools": environmentCheckSchema,
});
const formulatorChecksSchema = z.object({
    "ado-pat": environmentCheckSchema,
    "formulator-tools": environmentCheckSchema,
});
const helpChecksSchema = z.object({
    "help-tools": environmentCheckSchema,
});
const fogbugzChecksSchema = z.object({
    "fogbugz-tools": environmentCheckSchema,
});
const fogbugzFormulatorChecksSchema = z.object({
    "fogbugz-formulator-tools": environmentCheckSchema,
});
const askChecksSchema = z.object({
    "ask-tools": environmentCheckSchema,
});
const fogbugzToAdoChecksSchema = z.object({
    "fogbugz-to-ado-tools": environmentCheckSchema,
});
/** What `window.spectreeToolbox.app.checkEnvironment()` resolves to. */
export const environmentReportSchema = z.object({
    shared: sharedChecksSchema,
    /** Per-toolset checks; a toolset the app does not ship is absent. */
    toolsets: z.object({
        grooming: groomingChecksSchema.optional(),
        formulator: formulatorChecksSchema.optional(),
        /** Contract help 1. Absent from apps without the help toolset. */
        help: helpChecksSchema.optional(),
        /** Contract fogbugz 1. Absent from apps without the fogbugz toolset. */
        fogbugz: fogbugzChecksSchema.optional(),
        /** Contract fogbugz-formulator 1. Absent from apps without the fogbugz-formulator toolset. */
        "fogbugz-formulator": fogbugzFormulatorChecksSchema.optional(),
        /** Contract ask 1. Absent from apps without the ask toolset. */
        ask: askChecksSchema.optional(),
        /** Contract fogbugz-to-ado 1. Absent from apps without the fogbugz-to-ado toolset. */
        "fogbugz-to-ado": fogbugzToAdoChecksSchema.optional(),
    }),
});
/** True when every shared check is `ok` (or `info`). */
export function sharedChecksPass(report) {
    return SHARED_ENVIRONMENT_CHECKS.every((id) => report.shared[id].status !== "fail");
}
/** True when the shared checks and the toolset's required checks pass. */
export function isToolsetReady(report, toolset) {
    if (!sharedChecksPass(report))
        return false;
    const checks = report.toolsets[toolset];
    if (checks === undefined)
        return false;
    return TOOLSET_REQUIRED_CHECKS[toolset].every((id) => checks[id]?.status === "ok");
}
//# sourceMappingURL=environment.js.map